/*
  Colour schemes as the page scrolls. A section marked with snippets/page-scheme.liquid (data-page-scheme: the dark
  scheme or a coffee range's colours) puts the whole page, navigation included, in that scheme; elsewhere the page
  keeps its own (the theme's rules, a metafield or the visitor's device).

  - Marked data-page-scheme-line="middle" (the home Coffee row): the page blends towards the section's colours over
    the last 160 to 240px of scroll before the section reaches its place under the bar, so the section before it is
    read in its own colours on any screen, and back as it leaves through the middle, tied to the scroll itself,
    either way and at any speed. Fully there, the page is in its scheme.
  - Otherwise (Transparency): the page is in the scheme while the section is under the navigation bar (on phones, the
    compact header), and a page that opens on such a section starts in it.

  Settled, only the class and data-tone on <html> change (and the browser's theme colour), so every token and
  scheme-dark.css rule follows. Part way, each colour token is set on <html> as a blend of the two schemes, the dark
  rules following the scheme that is more than half there, and elements' own colour transitions pause so nothing
  trails behind. The shown colours follow the scroll with a short, smooth lag, so a wheel's steps don't show. A
  section can change its mark while on screen (a range chosen in the Coffee row) and fire page-schemes:update; the
  page then follows to the new colours the same way. Loaded by each section that marks
  itself; runs once however often included.
*/
if (!window.c4PageSchemes) {
  window.c4PageSchemes = true;
  const TOKENS = [
    'paper',
    'ink',
    'muted',
    'surface',
    'tile',
    'tile-cutout',
    'line',
    'bar',
    'bar-ink',
    'button',
    'button-ink',
  ];
  const html = document.documentElement;
  const meta = document.querySelector('meta[name="theme-color"]');
  const page = { className: html.className, tone: html.dataset.tone, color: meta?.content };
  const pageScheme = (page.className.match(/\bcolor-[\w-]+/) || [''])[0];
  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const mix = (a, b, p) => a.map((v, i) => v + (b[i] - v) * p);
  const hex = (c) =>
    '#' +
    c
      .map((v) =>
        Math.round(clamp(v) * 255)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('');
  /* A scheme's tokens as sRGB triples (0 to 1), read once from a probe wearing its class. */
  const schemes = new Map();
  const read = (className) => {
    if (!schemes.has(className)) {
      const probe = document.createElement('i');
      probe.className = className;
      probe.hidden = true;
      document.body.append(probe);
      schemes.set(
        className,
        TOKENS.map((token) => {
          probe.style.color = `var(--${token})`;
          const value = getComputedStyle(probe).color;
          const parts = value
            .match(/[\d.]+/g)
            .slice(0, 3)
            .map(Number);
          /* rgb() is 0 to 255; a mixed colour computes as color(srgb …), 0 to 1. */
          return value.startsWith('color(') ? parts : parts.map((v) => v / 255);
        }),
      );
      probe.remove();
    }
    return schemes.get(className);
  };
  /* While colours are part way, elements' own colour transitions would trail the scroll: pause them. "none" rather
     than a 0s duration, which keeps every element checking each transitioned property on every frame (2026-10-30:
     a blended frame's style pass took about twice as long that way). */
  const pause = document.createElement('style');
  pause.textContent =
    'html[data-scheme-blend] *, html[data-scheme-blend] *::before, html[data-scheme-blend] *::after { transition: none !important; }';
  document.head.append(pause);

  /* How far a section has taken over the page, 0 to 1. */
  const progress = (el, line) => {
    const r = el.getBoundingClientRect();
    if (el.dataset.pageSchemeLine !== 'middle') return r.top <= line && r.bottom > line ? 1 : 0;
    const zone = innerHeight * 0.3;
    const top = innerHeight / 2 - zone / 2;
    /* Coming in: over the last stretch before the section reaches its place under the bar (its top 60px below the
       bar), 160 to 240px of scroll however tall the window, so the section before it is read in its own colours
       and the change between passes quickly. */
    const run = Math.min(240, Math.max(160, innerHeight * 0.22));
    const enter = (line + 60 + run - r.top) / run;
    const leave = (r.bottom - top) / zone;
    return clamp(Math.min(enter, leave));
  };

  let shown = null;
  let last = 0;
  /* A chosen change (a range picked) blends quicker than the scroll's follow, and the same length whatever the colours. */
  let quick = 0;
  let queued = false;
  const apply = () => {
    queued = false;
    const marked = document.querySelectorAll('[data-page-scheme]');
    /* The navigation's fixed bar (a selector list would return c4-navigation itself, which scrolls away). On phones
       the bar is hidden (the compact header shows instead): take the line at the compact header's foot. */
    const bar =
      document.querySelector('c4-navigation .nav-bar') || document.querySelector('c4-navigation');
    const rect = bar?.getBoundingClientRect();
    const line = rect?.height ? rect.bottom : 64;
    let hit = null;
    let amount = 0;
    marked.forEach((el) => {
      const p = progress(el, line);
      if (p > amount) [hit, amount] = [el, p];
    });
    /* A page that opens on a section marked for the bar starts in its scheme, header and all. */
    const main = document.getElementById('MainContent');
    if (!hit && main) {
      const lead = [...marked].find(
        (el) =>
          el.dataset.pageSchemeLine !== 'middle' &&
          el.getBoundingClientRect().top - main.getBoundingClientRect().top < 2,
      );
      if (lead && lead.getBoundingClientRect().top > line) [hit, amount] = [lead, 1];
    }
    const target = hit ? `color-${hit.dataset.pageScheme}` : pageScheme;
    /* Eased at both ends, so the change starts and finishes gently rather than at a constant rate. */
    const eased = amount * amount * (3 - 2 * amount);
    const goal =
      amount > 0 && amount < 1
        ? read(pageScheme).map((c, i) => mix(c, read(target)[i], eased))
        : read(amount ? target : pageScheme);
    /* The shown colours follow the goal with a short lag (about a seventh of a second to close most of the gap), so a
       wheel's steps or a new range read as one smooth change; frames keep coming until they meet. */
    const now = performance.now();
    if (!shown || still.matches) shown = goal;
    else {
      const follow = 1 - Math.exp(-Math.min(64, now - last) / (now < quick ? 70 : 140));
      shown = shown.map((c, i) => mix(c, goal[i], follow));
    }
    last = now;
    const gap = Math.max(...shown.flatMap((c, i) => c.map((v, j) => Math.abs(v - goal[i][j]))));
    if (gap < (now < quick ? 0.012 : 0.004)) shown = goal;
    else queue();
    if (shown === goal && (amount === 0 || amount === 1)) {
      if (written) TOKENS.forEach((token) => html.style.removeProperty(`--${token}`));
      written = null;
      html.removeAttribute('data-scheme-blend');
      set(
        html,
        'className',
        amount ? page.className.replace(/\bcolor-[\w-]+/, target) : page.className,
      );
      set(html.dataset, 'tone', amount ? hit.dataset.pageTone : page.tone);
      if (meta) set(meta, 'content', amount ? hit.dataset.pageColor : page.color);
      return;
    }
    /* Every write to a token restyles the whole page, so only the ones whose shown colour moved are written: the
       last frames of a follow, and tokens the two schemes share, cost nothing. */
    const colours = shown.map(hex);
    set(html, 'className', page.className);
    if (!html.hasAttribute('data-scheme-blend')) html.setAttribute('data-scheme-blend', '');
    TOKENS.forEach((token, i) => {
      if (written?.[i] !== colours[i]) html.style.setProperty(`--${token}`, colours[i]);
    });
    written = colours;
    set(html.dataset, 'tone', hit && amount >= 0.5 ? hit.dataset.pageTone : page.tone);
    if (meta) set(meta, 'content', colours[0]);
  };
  /* The colours last written on <html>, as hex, or null while settled. */
  let written = null;
  const set = (target, key, value) => {
    if (target[key] !== value) target[key] = value;
  };
  const queue = () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(apply);
    }
  };
  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue);
  /* A section changed its mark: the shown colours follow to the new ones, from wherever the page is now. */
  document.addEventListener('page-schemes:update', () => {
    quick = performance.now() + 500;
    queue();
  });
  document.addEventListener('shopify:section:load', queue);
  queue();
}
